import { createContext, useContext } from 'react';

import { useActionInputState } from './hooks';
import { useRichInput } from './hooks/use-rich-input';
import { ActionData } from './lib/entities/action-data';
import { ImageAttachment, VideoAttachment } from './lib/entities/attachment';
import { Budget } from './lib/entities/budget';
import { RichMediaState } from './lib/state';
import { ActionValidator, ValidationError } from './lib/validation';
import { ActionModule } from './types';

export interface ActionInputContextValue extends ReturnType<typeof useActionInputState>, ReturnType<typeof useRichInput> {
	budget?: Budget;
	context: string; // 'recognition' | 'comment' | 'templated-award' | 'non-templated-award'
	giveAmount?: number; // for templated awards
	calculateTotalFn?: (data: RichMediaState, state: { includeGiver: boolean }) => number;
	calculateReceiversFn?: (includeGiver: boolean) => number;
	isSubmitting?: boolean;
	isLoadingAttachment?: boolean;
	cancelVideoGeneration?: () => void;
	modules: ActionModule[];
	onSubmit: (data: ActionData) => void;
	onUpload: (mediaFile: File, posterFile?: File | null) => Promise<VideoAttachment | ImageAttachment>;
	onValidationError?: (errors: ValidationError[]) => void;
	state: RichMediaState;
	validator?: ActionValidator;
}

const ActionInputContext = createContext<ActionInputContextValue>(null as any);

export const ActionInputContextProvider = ActionInputContext.Provider;

export const useActionInput = () => {
	const value = useContext(ActionInputContext);

	if (!value) {
		throw new Error('ActionInput context not found. Did you wrap your component in <ActionInput />');
	}

	return value;
};
